import type { ApiResponse } from './api-contract';

/**
 * SF-T-153: a time limit of the browser's own for requests the server answers only once a fleet
 * machine has (a patch's Apply or check, an agent's activity). The server gives the machine a time
 * limit, but an answer lost on the way back would otherwise leave a spinner going forever.
 */

/** For a read the server gives its machine 15 s for (the patch check, the agent's activity). */
export const MACHINE_READ_TIME_LIMIT_MS = 30_000;

export type TimeLimited<T> = { timedOut: false; res: T } | { timedOut: true };

/**
 * Sends `send` with a signal that aborts after `ms`, or when `outer` does (a loader superseding it:
 * then its own answer comes back, not a time-out). fetchApi answers an aborted request at once,
 * whether it was waiting on the session check or already sent; the race covers anything else that
 * could hang (a session renewal).
 */
export async function withTimeLimit<T>(send: (signal: AbortSignal) => Promise<T>, ms: number, outer?: AbortSignal | null): Promise<TimeLimited<T>> {
  const controller = new AbortController();
  const passOn = () => controller.abort();
  if (outer?.aborted) controller.abort();
  else outer?.addEventListener('abort', passOn);
  let expired = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ran = new Promise<'expired'>((resolve) => {
    timer = setTimeout(() => {
      expired = true;
      controller.abort();
      resolve('expired');
    }, ms);
  });
  try {
    const res = await Promise.race([send(controller.signal), ran]);
    if (res === 'expired' || expired) return { timedOut: true };
    return { timedOut: false, res };
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener('abort', passOn);
  }
}

/** What a request the browser stopped waiting on answers: describeApiError words it. */
export function timedOutResponse<T>(): ApiResponse<T> {
  return { status: 0, code: 'TIMEOUT', error: 'Software Factory took too long to answer. Try again.' };
}

/** An API call with a time limit: its own answer, or TIMEOUT once `ms` run out. */
export async function limitApiCall<T>(send: (signal: AbortSignal) => Promise<ApiResponse<T>>, ms: number, outer?: AbortSignal | null): Promise<ApiResponse<T>> {
  const out = await withTimeLimit(send, ms, outer);
  return out.timedOut ? timedOutResponse<T>() : out.res;
}
